import { validateIPv4IPv6WithMask } from "common/api";
import { enumerate } from "common/utils";
import { safeStr } from 'common/api';

const capitalize = (word) => word.charAt(0).toUpperCase() + word.slice(1);

const downcase = (word) => word.charAt(0).toLowerCase() + word.slice(1);

const lowerWord = (word, index) =>
  index === 0 ? word.toLowerCase() : capitalize(word);

const asJSAttribute = (input) => input.split(/\s|-+/).map(lowerWord).join("");

const significantCode = (code) =>
  code !== null && code !== undefined && code.length > 0;

const newBlock = (...codes) =>
  `\n${codes
    .filter(significantCode)
    .map((code) => code.trim("\n"))
    .join("\n")}\n`;

const _DEFAULT_BYPASS_SETTINGS = {
  ipv4: false,
  ipv6: false,
  untaggedVLANs: false,
  vlans: [],
  ipRanges: [],
  mpls: false,
  pppoe: false,
};
const includeBypassVLANSettings = ({ vlans = [] }, value) =>
  value === "untagged"
    ? { untaggedVLANs: true }
    : {
        vlans: enumerate(1, { stored: true })([
          ...vlans,
          { tag: parseInt(value) },
        ]),
      };

const includeBypassIPRangeSettings = ({ ipRanges = [] }, value) => ({
  ipRanges: enumerate(1, { stored: true })([...ipRanges, { address: value }]),
});

const includeBypassSetting = (settings, [bqn, field, value]) => ({
  ...settings,
  ...(field === "vlan"
    ? {
        [bqn]: {
          ...(settings[bqn] || _DEFAULT_BYPASS_SETTINGS),
          ...includeBypassVLANSettings(
            settings[bqn] || _DEFAULT_BYPASS_SETTINGS,
            value,
          ),
        },
      }
    : field === "iprange"
      ? {
          [bqn]: {
            ...(settings[bqn] || _DEFAULT_BYPASS_SETTINGS),
            ...includeBypassIPRangeSettings(
              settings[bqn] || _DEFAULT_BYPASS_SETTINGS,
              value,
            ),
          },
        }
      : {
          [bqn]: {
            ...(settings[bqn] || _DEFAULT_BYPASS_SETTINGS),
            [field]: asBoolYesNo(value),
          },
        }),
});

const _DEFAULT_SHAPING_SETTINGS = {
  perFlow: false,
  perSubscriber: false,
  ratePerSubscriber: false,
  ratePerSubscriberGroup: false,
};
const _DEFAULT_REDIRECTION_SETTINGS = {
  blockRedirectionIPv4URL: null,
  blockRedirectionIPv6URL: null,
};
const _DEFAULT_ACM_SETTINGS = { enabled: false };
const _DEFAULT_RTTI_SETTINGS = { small: 0.0, large: 0.0 };
const _DEFAULT_TCS_SETTINGS = { enabled: false, size: 0 };

export const _MPLS_BOTH = 'both';
export const _MPLS_L2 = 'l2';
export const _MPLS_L3 = 'l3';

const _DEFAULT_MPLS_SETTINGS = {
  value: _MPLS_BOTH,
  mplsEthernetFrame: false,
  mplsIPChecksum: false,
};


export const _DEFAULT_SETTINGS = {
  gre: false,
  tcp: false,
  adaptativeInitialWindow: _DEFAULT_TCS_SETTINGS,
  rtti: _DEFAULT_RTTI_SETTINGS,
  acm: _DEFAULT_ACM_SETTINGS,
  shaping: _DEFAULT_SHAPING_SETTINGS,
  mpls: _DEFAULT_MPLS_SETTINGS,
};
const parseBypassItem = (row) => {
  const [bqn, name, value] = row.trim().split(/\s+/);
  return [bqn, name, value];
};
const expectedBypassFields = [
  "ipv4",
  "ipv6",
  "iprange",
  "vlan",
  "mpls",
  "pppoe",
];

export const parseBypassParameters = (input) => {
  const [_head, ...rows] = input.trim("\n").split("\n");
  return rows
    .map(parseBypassItem)
    .filter(([_, name]) => expectedBypassFields.includes(name))
    .reduce(includeBypassSetting, {});
};

const includeRTTiParameter = (
  { rtti = _DEFAULT_RTTI_SETTINGS },
  field,
  value,
) => ({
  rtti: {
    ...rtti,
    [field === "speedRTTILarge" ? "large" : "small"]: Number(value).toFixed(3),
  },
});

const includeTCSParameter = (
  { adaptativeInitialWindow = _DEFAULT_TCS_SETTINGS },
  field,
  value,
) => ({
  adaptativeInitialWindow: {
    ...adaptativeInitialWindow,
    ...(field === "tcsInitialWindow"
      ? { size: parseInt(value) }
      : field === "tcsAdaptiveInitialWindow"
        ? { enabled: asBoolYesNo(value) }
        : {}),
  },
});
const includeACMParameter = ({ acm = _DEFAULT_ACM_SETTINGS }, value) => ({
  acm: {
    ...acm,
    enabled: value === "enabled",
  },
});
const includeShapingParameters = (
  { shaping = _DEFAULT_SHAPING_SETTINGS },
  field,
  value,
) => ({
  shaping: {
    ...shaping,
    [downcase(field.replace(/^shaping/, ""))]: asBoolYesNo(value),
  },
});

const includeMplsParameters = (
  { mpls = _DEFAULT_MPLS_SETTINGS },
  field,
  value,
) => {
  const attr = downcase(field);
  const boolValue = asBoolYesNo(value);

  return {
    mpls: {
      ...mpls,
      [attr]: boolValue,
      value: (attr === "mplsEthernetFrame" ? boolValue : mpls.mplsEthernetFrame)
        ? _MPLS_L2
        : (attr === "mplsIPChecksum" ? boolValue : mpls.mplsIPChecksum)
        ? _MPLS_BOTH
        : _MPLS_L3,
        },
  }
};
const valueOrNA = (input) => (input === "n/a" ? null : input);

const includeRedirectionParameters = (
  { redirection = _DEFAULT_REDIRECTION_SETTINGS },
  field,
  value,
) => {
  return {
    redirection: {
      ...redirection,
      [field]: valueOrNA(value.trim()),
    },
  }
};

const includeParameters = (settings, [field, value]) => ({
  ...settings,
  ...(field.startsWith("inspectGRE")
    ? { gre: value === "yes" }
    : field.startsWith("tcsOptimization")
    ? { tcp: value === "yes" }
    : field.startsWith("tcs")
    ? includeTCSParameter(settings, field, value)
    : field.startsWith("speed")
    ? includeRTTiParameter(settings, field, value)
    : field.startsWith("subscriberACM")
    ? includeACMParameter(settings, value)
    : field.startsWith("blockRedirectionIPv")
    ? includeRedirectionParameters(settings, field, value)
    : field.startsWith("shaping")
    ? includeShapingParameters(settings, field, value)
    : field.startsWith("mpls")
    ? includeMplsParameters(settings, field, value)
    : { [field]: value }),
});

const expectedParameterFields = [
  "inspectGRE",
  "tcsOptimization",
  "subscriberACM",
  "tcsAdaptiveInitialWindow",
  "tcsInitialWindow", //size
  "speedRTTILarge",
  "speedRTTISmall",
  "shapingPerFlow",
  "shapingPerSubscriber",
  "shapingRatePerSubscriber",
  "shapingRatePerSubscriberGroup",
  "mplsEthernetFrame",
  "mplsIPChecksum",
  "blockRedirectionIPv4URL",
  "blockRedirectionIPv6URL",
];

const involvedParameterFields =
  () =>
  ([name]) =>
    expectedParameterFields.includes(name);

const parseParameterItem = (row) => {
  const [name, value] = row.trim(/\s/).split(/:\s+/);
  return [asJSAttribute(name.split(" ").join("-")), value];
};

export const parseParameters = (input) => {
  return input
    .trim("\n")
    .split("\n")
    .map(parseParameterItem)
    .filter(involvedParameterFields())
    .reduce(includeParameters, {});
};

const asBoolYesNo = (value) =>
  value === "no" || value === "n/a"
    ? false
    : value === "yes"
      ? true
      : undefined;

/**************************************************
 * Build command
 **************************************************/
export class InvalidFieldValue extends Error {
  constructor(field, reason) {
    super(`the "${field}" field ${reason}`);
  }
}

const newOptimizationConfigurationBlock = (bqn, ...commands) =>
  newBlock("configure", "pkteng", ...addBqnId(commands, bqn), "commit").trim(
    "\n",
  );

const appliesToAllBqns = (command) =>
  command.startsWith("speed rtti-small") ||
  command.startsWith("speed rtti-large") ||
  command.includes("mpls ethernet-frame") ||
  command.includes("mpls ip-cksum") ||
  command.startsWith("block redirect") ||
  command.startsWith("no block redirect")

const addBqnId = (commands, bqnId) => {
  if (bqnId === undefined) {
    return commands;
  }

  return commands
    .filter(significantCode)
    .map((command) =>
      appliesToAllBqns(command) ? command : command + " bqn-id " + bqnId,
    );
};

const ensureSomeSettingsChanged = (permisive = false) =>
  permisive === true
    ? (...parts) => parts
    : (...parts) => {
        if (parts.flatMap((part) => (part === null ? [] : part)).length === 0) {
          throw "No changes available to commit.";
        }
        return parts;
      };

const isNotSavedOrDeleted = ({ stored, deleted }) =>
  stored !== true && deleted !== true;

const isSavedAndDeleted = ({ stored, deleted }) =>
  stored === true && deleted === true;

const notEmpty = (value) => {
  if (value === undefined || value.length === 0) {
    throw "can't be empty";
  }
  return value;
};

const verified = (label, value, rule) => {
  try {
    return rule(value);
  } catch (cause) {
    throw `${label} ${cause}`;
  }
};

const validIPAddressRange = (value) => {
  if (notEmpty(value) && validateIPv4IPv6WithMask(value)) {
    return value;
  }
  throw "is not a valid IPv4 range";
};

const validVLANTag = (value) => {
  if (notEmpty(value) === false) {
    throw "can't be empty";
  }
  const parsed = parseInt(value);
  if (isNaN(parsed)) {
    throw "has to be a number";
  }
  if (1 > parsed || parsed > 4094) {
    throw "can't be below 1 or above 4094";
  }
  return value;
};

const applyBypassMainSettings = (
  previous,
  { ipv4, ipv6, mpls, pppoe },
  metadata,
) => [
  ...(ipv4 === previous.ipv4
    ? []
    : ipv4 === true
    ? spreadToBqns("bypass ipv4", ipv4, metadata?.ipv4)
    : ipv4 === false
    ? spreadToBqns("no bypass ipv4", ipv4, metadata?.ipv4)
    : []),
  ...(ipv6 === previous.ipv6
    ? []
    : ipv6 === true
    ? spreadToBqns("bypass ipv6", ipv6, metadata?.ipv6)
    : ipv6 === false
    ? spreadToBqns("no bypass ipv6", ipv6, metadata?.ipv6)
    : []),
  ...(mpls === previous.mpls
    ? []
    : mpls === true
    ? spreadToBqns("bypass mpls", mpls, metadata?.mpls)
    : mpls === false
    ? spreadToBqns("no bypass mpls", mpls, metadata?.mpls)
    : []),
  ...(pppoe === previous.pppoe
    ? []
    : pppoe === true
    ? spreadToBqns("bypass pppoe", pppoe, metadata?.pppoe)
    : pppoe === false
    ? spreadToBqns("no bypass pppoe", pppoe, metadata?.pppoe)
    : []),
];

const composeSaveBypassVLANs = (vlans = [], metadata, onlineBqns = []) =>
  vlans
    .map(({ tag }) =>
      spreadToBqns(
        `bypass vlan ${verified(
          `Received VLAN tag "${tag}"`,
          tag,
          validVLANTag,
        )}`,
        true,
        metadata
          ? metadata[tag] ??
              onlineBqns.reduce((acc, { bqnId }) => {
                acc[bqnId] = false;
                return acc;
              }, {})
          : undefined,
      ),
    )
    .flat();

const composeClearBypassVLANs = (vlans, metadata) =>
  vlans
    .map(({ tag }) =>
      spreadToBqns(`no bypass vlan ${tag}`, false, metadata ? metadata[tag] : undefined),
    )
    .flat();

const composeBypassVLANsUpdate = (previous, vlans = [], metadata, onlineBqns) => [
  ...composeClearBypassVLANs(vlans.filter(isSavedAndDeleted), metadata),
  ...composeSaveBypassVLANs(vlans.filter(isNotSavedOrDeleted), metadata, onlineBqns),
];

const applyBypassVLANSettings = (previous, { vlans, untaggedVLANs }, metadata, onlineBqns) => [
  ...(untaggedVLANs === previous.untaggedVLANs
    ? []
    : untaggedVLANs === true
      ? spreadToBqns("bypass vlan untagged", untaggedVLANs, metadata?.untaggedVLANs)
      : untaggedVLANs === false
        ? spreadToBqns("no bypass vlan untagged", untaggedVLANs, metadata?.untaggedVLANs)
        : []),
  ...composeBypassVLANsUpdate(previous.vlans, vlans, metadata?.vlans, onlineBqns),
];

const composeSaveBypassIPRanges = (ipRanges = [], metadata, onlineBqns = []) =>
  ipRanges
    .map(({ address }) =>
      spreadToBqns(
        `bypass iprange ${verified(
          `Received IP Range "${address}"`,
          address,
          validIPAddressRange,
        )}`,
        true,
        metadata
          ? metadata[address] ??
              onlineBqns.reduce((acc, { bqnId }) => {
                acc[bqnId] = false;
                return acc;
              }, {})
          : undefined,
      ),
    )
    .flat();

const composeClearBypassIPRanges = (ipRanges, metadata) =>
  ipRanges
    .map(({ address }) =>
      spreadToBqns(`no bypass iprange ${address}`, false, metadata ? metadata[address]: undefined),
    )
    .flat();

const composeBypassIPRangesUpdate = (previous, ipRanges = [], metadata, onlineBqns) => [
  ...composeClearBypassIPRanges(ipRanges.filter(isSavedAndDeleted), metadata),
  ...composeSaveBypassIPRanges(ipRanges.filter(isNotSavedOrDeleted), metadata, onlineBqns),
];

const applyBypassSettings = (
  previous,
  { vlans, ipRanges, untaggedVLANs, ...settings },
  metadata,
  onlineBqns
) => [
  ...applyBypassMainSettings(previous, settings, metadata),
  ...applyBypassVLANSettings(previous, { vlans, untaggedVLANs }, metadata, onlineBqns),
  ...composeBypassIPRangesUpdate(previous.ipRanges, ipRanges, metadata?.ipRanges, onlineBqns),
];

const applyTCPSettings = (previous, { tcp }, metadata) =>
  previous.tcp === tcp
    ? [null]
    : tcp === true
    ? spreadToBqns("tcs optimization", tcp, metadata?.tcp)
    : tcp === false
    ? spreadToBqns("no tcs optimization", tcp, metadata?.tcp)
    : [null];

const applyGRESettings = (previous, { gre }, metadata) =>
  previous.gre === gre
    ? [null]
    : gre === true
    ? spreadToBqns("gre", gre, metadata?.gre)
    : gre === false
    ? spreadToBqns("no gre", gre, metadata?.gre)
    : [null];


const spreadToBqns = (command, value, metadata, whenValuesDiffer = true) => {
  if (metadata) {
    return Object.entries(metadata)
      .filter(([, currentValue]) => {
        return whenValuesDiffer
          ? currentValue !== value
          : currentValue === value;
      })
      .map(([bqnId]) => {
        return `${command} bqn-id ${bqnId}`;
      });
  } else {
    return [command];
  }
};
  

const applyACMSettings = (previous, { enabled = false }, metadata) =>
  previous.enabled === enabled
    ? [null]
    : enabled === true
    ? spreadToBqns("subscriber acm", enabled, metadata?.enabled)
    : enabled === false
    ? spreadToBqns("no subscriber acm", enabled, metadata?.enabled)
    : [null];

const shapingFieldAsCLI = {
  perFlow: "per-flow",
  perSubscriber: "per-subscriber",
  ratePerSubscriber: "subscriber-rate-limits",
  ratePerSubscriberGroup: "per-subscriber-group",
};

const applyShapingSettings = (previous, settings, metadata) =>
  Object.entries(settings).flatMap(([field, enabled]) =>
    previous[field] === enabled
      ? []
      : spreadToBqns(
          `${enabled ? "" : "no "}shaping ${shapingFieldAsCLI[field]}`,
          enabled,
          metadata ? metadata[field] : undefined,
        ),
  );

const applyMplsSettings = (previous, settings) => {
  if (previous.mplsEthernetFrame === true) {
    if (settings.value === _MPLS_BOTH) {
      if (previous.mplsIPChecksum !== true) {
        return ["no mpls ethernet-frame", "mpls ip-cksum"];
      } else {
        return ["no mpls ethernet-frame"];
      }
    }
    if (settings.value === _MPLS_L3) {
      if (previous.mplsIPChecksum !== true) return ["no mpls ethernet-frame"];
      else return ["no mpls ethernet-frame", "no mpls ip-cksum"];
    }
  } else if (previous.mplsIPChecksum === true) {
    if (settings.value === _MPLS_L2) {
      return ["mpls ethernet-frame", "no mpls ip-cksum"];
    }
    if (settings.value === _MPLS_L3) {
      return ["no mpls ip-cksum"];
    }
  } else {
    if (settings.value === _MPLS_L2) {
      return ["mpls ethernet-frame"];
    }
    if (settings.value === _MPLS_BOTH) {
      return ["mpls ip-cksum"];
    }
  }

  return [];
}
const redirectionFieldAsCLI = {
  blockRedirectionIPv4URL: "ipv4",
  blockRedirectionIPv6URL: "ipv6",
};

const preventWhiteSpaces = (value, fieldName) => {
  if (value.includes(" ") === true) {
    throw new InvalidFieldValue(fieldName, `value "${value}" contains spaces`);
  }
  return value;
};

const safeURL = (value, fieldName) => safeStr(preventWhiteSpaces(value, fieldName));

const applyShapingRedirection = (previous, settings) => {
  if(previous === undefined && settings === undefined){
    return [];
  }
  
  return Object.entries(settings).flatMap(([field, value]) => {
    return previous[field] === value ||
      (previous[field] === null && value === "")
      ? []
      : [
          `${value === "" || value === null ? "no " : ""}block redirect ${redirectionFieldAsCLI[field]}${value ? ` ${safeURL(value, "Redirection URL")}` : ""}`,
        ];
  });
};

const validIWSize = (value) => {
  if (notEmpty(value) === false) {
    throw "can't be empty";
  }
  const parsed = parseInt(value);
  if (isNaN(parsed)) {
    throw "has to be a number";
  }
  if (0 > parsed) {
    throw "can't be 0 or below";
  }
  if (100 < parsed) {
    throw "can't be above 100";
  }
  return value;
};
const applyAIWSettings = (previous, { enabled, size }, metadata) => [
  ...(enabled === previous.enabled
    ? []
    : enabled === true
    ? spreadToBqns("no tcs no-adaptive-init-wnd", enabled, metadata?.enabled)
    : enabled === false
    ? spreadToBqns("tcs no-adaptive-init-wnd", enabled, metadata?.enabled)
    : []),
  ...(size === previous.size
    ? []
    : size === 10
    ? spreadToBqns(
        `no tcs init-wnd ${previous.size}`,
        previous.size,
        metadata?.size,
        false,
      )
    : spreadToBqns(
        `tcs init-wnd ${verified(
          "Received Initial window",
          size,
          validIWSize,
        )}`,
        size,
        metadata?.size,
      )),
];

const validRTTIValue = (value) => {
  if (notEmpty(value) === false) {
    throw "can't be empty";
  }
  const parsed = parseFloat(value);
  if (isNaN(parsed)) {
    throw "has to be a float number";
  }
  if (0 > parsed) {
    throw "can't be 0 or below";
  }
  return value;
};

const applyRTTISettings = (previous, { small, large }) => [
  ...(small === previous.small
    ? []
    : [
        `speed rtti-small ${verified(
          "Received RTTi-small parameter",
          small,
          validRTTIValue,
        )}`,
      ]),
  ...(large === previous.large
    ? []
    : [
        `speed rtti-large ${verified(
          "Received RTTi-large parameter",
          large,
          validRTTIValue,
        )}`,
      ]),
];

export const composeApplyCommand = (previous, settings, bqn, metadata, onlineBqns) =>
  newOptimizationConfigurationBlock(
    metadata ? undefined : bqn,
    ...ensureSomeSettingsChanged(previous.enabled !== settings.enabled)(
      ...applyGRESettings(previous, settings, metadata),
      ...applyTCPSettings(previous, settings, metadata),
      ...applyACMSettings(previous.acm, settings.acm, metadata?.acm),
      ...applyShapingSettings(previous.shaping, settings.shaping, metadata?.shaping),
      ...applyMplsSettings(previous.mpls, settings.mpls),
      ...applyShapingRedirection(previous.redirection, settings.redirection),
      ...applyAIWSettings(
        previous.adaptativeInitialWindow,
        settings.adaptativeInitialWindow,
        metadata?.adaptativeInitialWindow,
      ),
      ...applyRTTISettings(previous.rtti, settings.rtti),
      ...applyBypassSettings(previous.bypass, settings.bypass, metadata?.bypass, onlineBqns),
    ),
  );
